import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import CommunityBanner from '../components/CommunityBanner';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: 'easeOut' },
  },
};

const values = [
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    title: 'Honest Service',
    desc: 'We tell you what your vehicle actually needs. No upselling, no unnecessary work. Just straight advice you can trust from people who care about your safety.',
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
      </svg>
    ),
    title: 'Quality Workmanship',
    desc: 'Every job is done right the first time. We use quality parts and follow best practice so your vehicle leaves in better shape than when it arrived.',
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    title: 'Local and Proud',
    desc: 'We are part of the Kapiti Coast community. Our customers are our neighbours, and we take that seriously. Supporting local means more to us than just a tagline.',
  },
];

const stats = [
  { value: '2006', label: 'Founded' },
  { value: '20+', label: 'Years in Business' },
  { value: '5', label: 'Qualified Technicians' },
  { value: '7', label: 'Team Members' },
];

export default function About() {
  return (
    <div style={{ backgroundColor: '#0A0F1C' }}>
      {/* Page header */}
      <section
        className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 text-center"
        style={{ backgroundColor: '#070C18', borderBottom: '1px solid rgba(204,0,0,0.2)' }}
      >
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#CC0000' }}>
            Who We Are
          </p>
          <h1
            className="text-5xl sm:text-6xl font-bold text-white mb-4"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Your Local Kapiti Coast Mechanics
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Founded in 2006, built from the ground up, and proud to still be serving Waikanae over 20 years on.
          </p>
        </motion.div>
      </section>

      {/* Story section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-start gap-14">

            {/* About image */}
            <motion.div
              className="w-full lg:w-1/2 flex-shrink-0"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <img
                src="/hero.jpg"
                alt="Auto Doctor workshop team, Waikanae"
                className="w-full rounded-2xl"
                style={{ height: '460px', objectFit: 'cover', objectPosition: 'center' }}
              />
            </motion.div>

            {/* Story text */}
            <motion.div
              className="w-full lg:w-1/2"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#CC0000' }}>
                Our Story
              </p>
              <h2
                className="text-4xl sm:text-5xl font-bold text-white mb-6"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Built from the Ground Up
              </h2>
              <div className="space-y-4 text-gray-300 text-base leading-relaxed mb-8">
                <p>
                  Mike and Joe Bennett founded Auto Doctor in 2006, starting from a small workshop in Waikanae with a clear vision: to give the Kapiti Coast community a workshop they could actually trust. From day one, the focus was honest advice, quality work, and treating every customer fairly.
                </p>
                <p>
                  After 11 years of steady growth and a loyal customer base built entirely on reputation, Mike and Joe purchased the section next door and built a purpose-built workshop from the ground up, designed specifically for the way the team works.
                </p>
                <p>
                  Today, Auto Doctor is home to 5 fully qualified technicians and 2 dedicated office staff, Trish and Fee, who keep everything running smoothly behind the scenes. Over 20 years in, the same values that founded the business still drive everything we do: be honest, do the job right, and look after our community.
                </p>
                <p>
                  We are proud sponsors of the Waikanae Rugby Club, Kapiti Softball Club, and both Waikanae Lawn Bowls Clubs, because supporting local sport and community organisations is something we genuinely believe in, not just a marketing exercise.
                </p>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {stats.map((s, i) => (
                  <motion.div
                    key={i}
                    className="rounded-xl p-4 text-center"
                    style={{ backgroundColor: '#0D1220', border: '1px solid rgba(204,0,0,0.25)' }}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={i}
                    variants={{
                      hidden: { opacity: 0, y: 20 },
                      visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.1 } },
                    }}
                  >
                    <p
                      className="text-2xl font-bold mb-1"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#CC0000' }}
                    >
                      {s.value}
                    </p>
                    <p className="text-gray-400 text-xs leading-tight">{s.label}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Community section */}
      <CommunityBanner />

      {/* Values */}
      <section style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            className="text-center mb-14"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#CC0000' }}>
              What Drives Us
            </p>
            <h2
              className="text-4xl sm:text-5xl font-bold"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#0A0F1C' }}
            >
              Our Values
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((val, i) => (
              <motion.div
                key={i}
                className="rounded-2xl p-8 text-center"
                style={{ backgroundColor: '#0A0F1C', border: '1px solid rgba(204,0,0,0.25)' }}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={{
                  hidden: { opacity: 0, y: 40 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.12, ease: 'easeOut' } },
                }}
              >
                <div
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-5"
                  style={{ backgroundColor: 'rgba(204,0,0,0.12)', color: '#CC0000' }}
                >
                  {val.icon}
                </div>
                <h3
                  className="text-2xl font-bold text-white mb-3"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {val.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">{val.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reputation callout */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#070C18', borderTop: '1px solid rgba(204,0,0,0.2)' }}>
        <motion.div
          className="max-w-3xl mx-auto text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <h2
            className="text-4xl sm:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Our Reputation Speaks for Itself
          </h2>
          <p className="text-gray-400 text-lg mb-8 leading-relaxed">
            Highly rated on Google and 100% recommended by customers on Facebook. Read what the Kapiti Coast community has to say about their experience with Auto Doctor.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/testimonials"
              className="inline-block px-8 py-3 font-semibold text-white rounded transition-all duration-200"
              style={{ backgroundColor: '#CC0000' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#AA0000'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#CC0000'; }}
            >
              Read Reviews
            </Link>
            <Link
              to="/contact"
              className="inline-block px-8 py-3 font-semibold text-white rounded border-2 transition-all duration-200"
              style={{ borderColor: '#CC0000' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#CC0000'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              Book a Service
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
